/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import { IPriceComponentResources } from '@msdyn365-commerce/components';
import * as Msdyn365 from '@msdyn365-commerce/core';
import MsDyn365, { ICoreContext } from '@msdyn365-commerce/core';
import { ProductPrice } from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    DimensionAvailabilitiesForProductSearchResultInput,
    DimensionTypes,
    getDimensionAvailabilitiesForProductSearchResultAction,
    getPriceForProductSearchResult,
    IDimensionsApp,
    ObjectExtensions,
    PriceForProductSearchResultInput
} from '@msdyn365-commerce-modules/retail-actions';
import {
    getTelemetryObject,
    IComponentNodeProps,
    IModuleProps,
    INodeProps,
    ISingleSlideCarouselProps,
    ITelemetryContent,
    NodeTag
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IOnlyCProductCollectionData } from './only-c-product-collection.data';
import { IOnlyCProductCollectionProps, layout } from './only-c-product-collection.props.autogenerated';
import ProductComponent from '../../themes/only/views/components/product.component';
import SingleSlideCarousel from '../../themes/only/views/custom-components/SingleSlideCarousel.component';
import { ProductSearchResultExtended } from '../only-c-search-result-container/actions/get-full-products-by-collection';

export interface IProductCollectionViewProps extends IOnlyCProductCollectionProps<IOnlyCProductCollectionData> {
    heading?: React.ReactNode;
    ProductCollectionContainer: IModuleProps;
    SingleSlideCarouselComponentProps: INodeProps;
    GridComponentProps: INodeProps;
    products: IProductComponentViewProps[];
    isCarousel: boolean;
    productPrices?: ProductPrice[];
}

export interface IProductComponentViewProps {
    ProductContainer: INodeProps;
    productComponent: React.ReactNode;
}
export interface IProductComponentState {
    products: ProductSearchResultExtended[];
    productsDimensionAvailabilities?: IProductsDimensionsAvailabilities[][];
}

/**
 * ProductCollection component.
 */
class ProductCollection extends React.PureComponent<IOnlyCProductCollectionProps<IOnlyCProductCollectionData>, IProductComponentState> {
    private readonly telemetryContent: ITelemetryContent;

    private readonly productPrices: ProductPrice[];

    constructor(props: IOnlyCProductCollectionProps<IOnlyCProductCollectionData>) {
        super(props);
        this.productPrices = [];
        this.state = {
            products: this.props.config.productCollection.products
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }
    public componentDidMount(): void {
        if (this.props.config.enableAffiliationBasedPricing && this.props.context.actionContext.requestContext.user.isAuthenticated) {
            this._getActivePrice().catch(ex => this.props.telemetry.error(ex));
        }

        if (MsDyn365.isBrowser && document.addEventListener) {
            // VSI Customization to prevent empty Image display, extended to update state
            let products = this.state.products.slice();
            let timeOut: NodeJS.Timeout;
            document.addEventListener('DOMNodeInserted', (event: Event) => {
                const target = event.target as HTMLElement;
                const item =
                    target?.classList?.contains('msc-empty_image') &&
                    target?.closest('li')?.classList?.contains('ms-product-collection__item');
                if (item) {
                    // setting to hidden is redundant as product will be reomved from state and module rerenderes without it
                    // however it is faster, so hopefully can avoud showing empty images
                    target.closest('li')?.classList?.add('hidden');
                    const itemId = target.closest('li')?.attributes['itemId'].value;
                    const colour = target.closest('li')?.attributes['colour'].value;
                    // remove from state so that carousel re-renders (otherwise button clicks through items are unreliable)
                    if (itemId && colour) {
                        products = products.filter(product => product.ItemId !== itemId || product.variantColor !== colour);
                        // debounce state updates to because react can't batch them inside native event handler
                        // and don't want excessive re-renders
                        if (timeOut) {
                            clearTimeout(timeOut);
                        }
                        timeOut = setTimeout(() => {
                            this.setState({ products: products });
                        }, 50);
                    }
                }
            });
        }
    }

    public render(): JSX.Element | null {
        const { heading, className } = this.props.config;
        // const { resources } = this.props;

        // Initiallize state in case if the products were not found during initial module render
        this.initProductState(this.props.config.productCollection).catch(ex => this.props.telemetry.error(ex));

        const products = this.state.products;
        const isCarousel = this.props.config.layout === layout.carousel;
        const headingLabel = heading ? heading.text : '';
        const headingComponent = heading && (
            <Msdyn365.Text
                className='ms-product-collection__heading'
                tag={heading.tag || 'h1'}
                text={headingLabel}
                editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
            />
        );

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- product check for safety.
        const updatedProducts = products?.map((item: ProductSearchResultExtended, index: number) => {
            const getProductData = this._getProduct(item, index);
            return !ObjectExtensions.isNullOrUndefined(getProductData) ? getProductData : undefined;
        });

        const productCollectionViewProps = {
            ...this.props,
            heading: headingComponent,
            SingleSlideCarouselComponentProps: {
                tag: SingleSlideCarousel,
                className: '',
                // flipperPrevLabel: `${headingLabel} ${resources.flipperPrevious}`,
                // flipperNextLabel: `${headingLabel} ${resources.flipperNext}`,
                parentId: this.props.id,
                telemetryContent: this.telemetryContent
            } as IComponentNodeProps<ISingleSlideCarouselProps>,
            ProductCollectionContainer: {
                moduleProps: this.props,
                className: classnames('ms-product-collection', className)
            },
            GridComponentProps: {
                tag: 'ul' as NodeTag,
                className: 'ms-product-collection__items'
            },
            isCarousel,

            products:
                ArrayExtensions.hasElements(updatedProducts) &&
                updatedProducts.filter(item => {
                    return item !== undefined;
                }),
            productPrices: this.productPrices
        };
        if (products) {
            const itemIds = Array.from(new Set(products.map(item => item.ItemId)));
            let variantsToShow = this.props.config.variantsToShow ?? 1;
            if (variantsToShow < 1) {
                variantsToShow = 1;
            }
            return (
                <>
                    {/* Idea was to tag li elements with product item id class, then can hide any variants over 3rd with nth-child,
                but nth-child doesn't take class into accout so using sibling combinator instead
                Thanks to https://betterprogramming.pub/css-select-nth-element-with-class-a313d080e2bf */}
                    <style type='text/css'>
                        {itemIds.map(itemId => {
                            let cssSelector = `li.item-${itemId}`;
                            for (let i = 0; i < variantsToShow; i++) {
                                cssSelector += ` ~ li.item-${itemId}`;
                            }
                            return <>{`${cssSelector} {display:none;}`}</>;
                        })}
                    </style>
                    {this.props.renderView(productCollectionViewProps) as React.ReactElement}
                </>
            );
        }
        return null;
    }

    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.heading!.text = event.target.value);

    /**
     * Get products dimension availabilities.
     * @param products - Products.
     * @returns Promise.
     */
    private readonly _getProductsDimensionsAvailabilities = async (products: ProductSearchResultExtended[]): Promise<void> => {
        const context = this.props.context as ICoreContext<IDimensionsApp>;
        const dimensionsToDisplayOnProductCard = context.app.config.dimensionsInProductCard;
        const dimensionsToDisplayAsSwatch = context.app.config.dimensionsAsSwatchType;
        const validProducts = ArrayExtensions.validValues(products);
        if (
            ArrayExtensions.hasElements(validProducts) &&
            ArrayExtensions.hasElements(dimensionsToDisplayOnProductCard) &&
            !dimensionsToDisplayOnProductCard.includes(DimensionTypes.none) &&
            ArrayExtensions.hasElements(dimensionsToDisplayAsSwatch) &&
            !dimensionsToDisplayAsSwatch.includes(DimensionTypes.none)
        ) {
            const productsDimensionAvailabilities = await getDimensionAvailabilitiesForProductSearchResultAction(
                new DimensionAvailabilitiesForProductSearchResultInput(validProducts, this.props.context as ICoreContext<IDimensionsApp>),
                this.props.context.actionContext
            );

            if (ArrayExtensions.hasElements(productsDimensionAvailabilities)) {
                this.setState({ productsDimensionAvailabilities });
            }
        }
    };

    /**
     * Helper method to initialize products state.
     * @param productCollection - List of products.
     */
    // @ts-ignore
    private readonly initProductState = async (productCollection: Msdyn365.IProductList) => {
        let products = this.state.products;
        const productCollectionProducts = productCollection.products;
        if (!ArrayExtensions.hasElements(products) && ArrayExtensions.hasElements(productCollectionProducts)) {
            products = productCollectionProducts;
            this.setState({ products });
        }

        if (!ArrayExtensions.hasElements(this.state.productsDimensionAvailabilities)) {
            await this._getProductsDimensionsAvailabilities(products);
        }
    };

    private readonly _getProduct = (product: ProductSearchResultExtended, index: number): IProductComponentViewProps | null => {
        if (!product || !product.Name) {
            this.props.telemetry.trace('[product-collection] Product or product name is null');
            return null;
        }
        const telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        const { imageSettings, allowBackNavigation } = this.props.config;
        const resources = this.props.resources;
        const breaCrumbType = this.props.context.app.config.breadcrumbType;
        const allowBack = allowBackNavigation && (breaCrumbType === 'back' || breaCrumbType === 'categoryAndBack');
        const quickviewSlot =
            this.props.slots && this.props.slots.quickview && this.props.slots.quickview.length > 0 ? this.props.slots.quickview[0] : null;
        const reviewCount = 1;
        const reviewTotalRatings = Number(product.TotalRatings);
        const reviewCountAriaLabelValue =
            reviewTotalRatings > reviewCount ? resources.ratingCountAriaLabel : resources.ratingCountAriaLabelSingleUser;
        const isPriceMinMaxEnabled = this.props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
        );
        const priceResources: IPriceComponentResources = {
            priceRangeSeparator: resources.priceRangeSeparator
        };
        return {
            ProductContainer: {
                tag: 'li' as NodeTag,
                className: `ms-product-collection__item item-${product.ItemId}`,
                role: 'listitem',
                key: index,
                itemId: product.ItemId,
                colour: product.variantColor
            },
            productComponent: (
                <ProductComponent
                    context={this.props.context}
                    telemetryContent={telemetryContent}
                    imageSettings={imageSettings}
                    freePriceText={resources.priceFree}
                    originalPriceText={resources.originalPriceText}
                    currentPriceText={resources.currentPriceText}
                    ratingAriaLabel={resources.ratingAriaLabel}
                    ratingCountAriaLabel={reviewCountAriaLabelValue}
                    allowBack={allowBack}
                    id={this.props.id}
                    typeName={this.props.typeName}
                    data={{ product }}
                    quickViewButton={quickviewSlot}
                    isPriceMinMaxEnabled={isPriceMinMaxEnabled?.IsEnabled}
                    priceResources={priceResources}
                    //   VSI Customization - Start
                    newArrivalMsg={resources.newArrivalMsg}
                    productLifeTag={resources.productLifeTag}
                    maxSizeDisplay={9}
                    splitSizeText={resources.splitSizeText}
                    colorText={resources.colorText}
                    // END
                    dimensionAvailabilities={this.state.productsDimensionAvailabilities?.find(
                        dimensionAvailability =>
                            ArrayExtensions.hasElements(dimensionAvailability) &&
                            dimensionAvailability[0].masterProductId === (product.MasterProductId ?? product.RecordId)
                    )}
                />
            )
        };
    };

    /**
     * Get the active price for products.
     */
    private readonly _getActivePrice = async (): Promise<void> => {
        const productsList = this.state.products;
        const products = await getPriceForProductSearchResult(
            new PriceForProductSearchResultInput(productsList),
            this.props.context.actionContext
        );

        await this._getProductsDimensionsAvailabilities(products);

        this.setState({ products });
    };
}

export default ProductCollection;
